<template>
  <MoleculeMatrixEvents :uris="uris" :title="title" :button-label="buttonLabel" :button-url="buttonUrl" :color-theme="colorTheme" />
</template>

<script setup lang="ts">
import { toLocalDateString } from '~/assets/util'
import { toHour } from '@/assets/date'

const props = defineProps({
  buttonLabel: { type: String, required: false, default: undefined },
  buttonUrl: { type: String, required: false, default: undefined },
  colorTheme: { type: String, required: false, default: undefined },
  composers: { type: Array as PropType<any[]>, required: false, default: undefined },
  eventTypes: { type: Array as PropType<any[]>, required: false, default: undefined },
  genres: { type: Array as PropType<any[]>, required: false, default: undefined },
  hideSoldOutConcerts: { type: Boolean, required: false, default: false },
  instruments: { type: Array as PropType<any[]>, required: false, default: undefined },
  keywords: { type: String, required: false, default: undefined },
  maximumItems: { type: Number, required: false, default: 15 },
  organizers: { type: Array as PropType<any[]>, required: false, default: undefined },
  rooms: { type: Array as PropType<any[]>, required: false, default: undefined },
  searchFromDate: { type: String, required: false, default: undefined },
  specials: { type: Array as PropType<any[]>, required: false, default: undefined },
  tags: { type: Array as PropType<any[]>, required: false, default: undefined },
  title: { type: String, required: false, default: undefined },
})

const { locale } = useI18n()
const graphqlFetch = useGraphqlFetch()

/** GraphQL query variables; `importTixSaleStatus` is only added when sold-out concerts are hidden. */
const variables: CmsData = {
  site: `${locale.value}Default`,
  limit: props.maximumItems,
  gteDate: `gte ${toLocalDateString(props.searchFromDate, toLocalDateString(toHour('now')))}`,
  search: props.keywords,
  eventTypes: props.eventTypes?.map((value) => value.title) || [],
  genres: props.genres?.map((value) => value.title) || [],
  organizers: props.organizers?.map((value) => value.title) || [],
  composers: props.composers?.map((value) => value.title) || [],
  instruments: props.instruments?.map((value) => value.title) || [],
  rooms: props.rooms?.map((value) => value.title) || [],
  tags: props.tags?.map((value) => value.title) || [],
  specials: props.specials?.map((value) => value.title) || [],
  importIconcertStatus: ['1e Optie', 'Bevestigd', 'Contract uit', 'Corona', 'Voor brochure'],
}

if (props.hideSoldOutConcerts) {
  variables.importTixSaleStatus = ['NoStatus', 'FewTickets']
}

const { data: uris } = await useAsyncData(
  `matrix-search-events;${JSON.stringify(variables)}`,
  async () => {
    const { elasticSearch } = await graphqlFetch({
      token: 'elasticsearch',
      query: `
        query matrixSearchEvents(
          $site: [String]
          $limit: Int
          $gteDate: [String]
          $search: String
          $eventTypes: [String]
          $genres: [String]
          $organizers: [String]
          $composers: [String]
          $instruments: [String]
          $rooms: [String]
          $tags: [String]
          $specials: [String]
          $importIconcertStatus: [String]
          $importTixSaleStatus: [String]
        ) {
          elasticSearch(
            section: "event"
            site: $site
            eventDateRange: $gteDate
            search: $search
            eventTypeFacet: $eventTypes
            genresFacet: $genres
            organizerFacet: $organizers
            composersFacet: $composers
            instrumentsFacet: $instruments
            roomFacet: $rooms
            tagFacet: $tags
            specialsFacet: $specials
            importIconcertStatusFacet: $importIconcertStatus
            importTixSaleStatusTextFacet: $importTixSaleStatus
            orderBy: ["eventDate ASC", "room ASC"]
          ) {
            hits(limit: $limit) {
              uri
            }
          }
        }
      `,
      variables,
    })

    return elasticSearch.hits.map(({ uri }: CmsData) => uri)
  },
  { default: () => [] },
)
</script>
